'use client';
import { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { toast } from "sonner";
import { Loader2, Mail } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import z from "zod";
import { Form, FormField, FormItem, FormLabel, FormMessage } from "../ui/form";
import { signUp, useSession } from "@/lib/auth-client";
import { useTRPC } from "@/lib/trpc";
import { useMutation } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { Logo } from "../logo";
import Link from "next/link";

interface SignUpStepProps {
  onSignUp: () => void;
}

const signInSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
  password: z.string().min(1, "Password is required"),
});

export function SignUpStep({ onSignUp }: SignUpStepProps) {
  const trpc = useTRPC();
  const router = useRouter();
  const [isSigningUp, setIsSigningUp] = useState<boolean>(false);
  const { data: session, isPending: isSessionPending } = useSession();

  const form = useForm<z.infer<typeof signInSchema>>({
    defaultValues: {
      email: "",
      password: "",
    },
    resolver: zodResolver(signInSchema),
  });

  // Check email exists mutation
  const checkEmailMutation = useMutation(
    trpc.onboarding.checkEmailExists.mutationOptions()
  );

  // Get onboarding status after successful sign-in




  const onSubmit = async (data: z.infer<typeof signInSchema>) => {

    try {
      // Attempt to sign in
      const result = await signUp.email({
        name: '',
        email: data.email,
        password: data.password,


      }, {
        onRequest: (ctx) => {
          setIsSigningUp(true);
         }, onSuccess: (ctx) => {
          console.log(ctx);
          onSignUp();
        }, onError: () => {
          setIsSigningUp(false);

          toast.error("An unexpected error has occurred. ")
        }
      });



      // Sign-in succeeded - wait for session and check onboarding status
      // The useEffect will handle the rest when session is available
    } catch (error) {

    }
  };

  return (
    <div className="max-w-sm w-full mx-auto space-y-8 flex items-center justify-center h-full">
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="w-full">
          <div className="flex flex-col gap-6">
            <div className="flex flex-col items-center gap-2">
              <Logo className="h-6 w-6 text-primary" size={64} />
              <h1 className="text-2xl font-bold">Get Started</h1>
              <p className="text-center text-sm text-muted-foreground">
                Enter your email and password to continue
              </p>
            </div>

            <FormField
              name="email"
              control={form.control}
              render={({ field }) => (
                <FormItem className="grid gap-2">
                  <FormLabel htmlFor="email">Email</FormLabel>
                  <Input
                    {...field}
                    id="email"
                    type="email"
                    placeholder="you@example.com"
                    disabled={isSigningUp}
                    autoFocus
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              name="password"
              control={form.control}
              render={({ field }) => (
                <FormItem className="grid gap-2">
                  <FormLabel htmlFor="password">Password</FormLabel>
                  <Input
                    {...field}
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    disabled={isSigningUp}
                  />
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button type="submit" disabled={isSigningUp} className="w-full">
              {isSigningUp ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                 <span>Creating Account...</span>
                </>
              ): <>Create Account</>}
            </Button>

            <div className="text-center text-sm">
              <span>Already have an account? </span>
              <Link href="/login"
                
                  prefetch={true}
                className="underline underline-offset-4"
              >
                Sign In
              </Link>
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}

